import React from "react";
import { FileCode2, Link2, Info } from "lucide-react";
import { AdminSection } from "./adminSections";
import { FieldEditor } from "./FieldEditor";
import { ImageFieldAdmin } from "./ImageFieldAdmin";

interface AdminSectionCardProps {
  section: AdminSection;
  getValue: (key: string, fallback: string) => string;
  onChange: (key: string, value: string) => void;
}

export const AdminSectionCard: React.FC<AdminSectionCardProps> = ({
  section,
  getValue,
  onChange,
}) => {
  return (
    <section
      id={`admin-section-${section.id}`}
      data-testid={`admin-section-${section.id}`}
      className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="font-display font-semibold text-lg text-motor-ink">{section.title}</h2>
        <div className="flex flex-wrap gap-1.5">
          {section.filePaths.map((p) => (
            <span
              key={p}
              className="inline-flex items-center gap-1 rounded-full bg-motor-mist px-2.5 py-1 text-[11px] font-mono text-neutral-600"
            >
              <FileCode2 className="h-3 w-3" />
              {p}
            </span>
          ))}
        </div>
      </div>

      {section.note && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {section.note}
        </p>
      )}

      {section.linkRefs && section.linkRefs.length > 0 && (
        <div className="mt-4 flex flex-col gap-1.5">
          {section.linkRefs.map((ref) => (
            <div key={ref.label} className="flex items-start gap-2 text-xs text-neutral-600">
              <Link2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-motor-red" />
              <span className="font-semibold">{ref.label}:</span>
              <span className="font-mono break-all">{ref.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 grid gap-4">
        {section.fields.map((field) => (
          <FieldEditor
            key={field.key}
            field={field}
            value={getValue(field.key, field.value)}
            onChange={onChange}
          />
        ))}
      </div>

      {section.images && section.images.length > 0 && (
        <div className="mt-5 grid gap-3">
          {section.images.map((img) => (
            <ImageFieldAdmin key={img.key} image={img} />
          ))}
        </div>
      )}
    </section>
  );
};
